import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Check, X, Gift, Flame, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useLang } from '../context/LanguageContext';
import { MaskedLines, Reveal, SectionHead } from '../components/motion';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { createCheckout } from '../lib/api';
import PayPalButton from '../components/PayPalButton';

const IMG_HERO = 'https://images.pexels.com/photos/34862859/pexels-photo-34862859.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';
const IMG_INSTRUCTOR = 'https://customer-assets.emergentagent.com/job_e3e473e3-754d-454b-b5d4-98ee896822e8/artifacts/qofer82u_photo_2025-10-08_14-24-08.jpg';
const IMG_BEGINNER = 'https://images.unsplash.com/photo-1635236190542-d43e4d4b9e4b';
const IMG_PRO = 'https://images.pexels.com/photos/6771900/pexels-photo-6771900.jpeg';
const IMG_INCOME = 'https://images.pexels.com/photos/6801874/pexels-photo-6801874.jpeg';
const IMG_CASE = 'https://customer-assets.emergentagent.com/job_open-preview-5/artifacts/amnm3qaa_photo_2026-05-29_17-48-32.jpg';

const FOR_IMGS = [IMG_BEGINNER, IMG_PRO, IMG_INCOME];

function useCountdown() {
  const [deadline] = useState(() => {
    const saved = Number(localStorage.getItem('mc_deadline'));
    const now = Date.now();
    if (saved && saved > now) return saved;
    const d = now + 2 * 60 * 60 * 1000;
    localStorage.setItem('mc_deadline', String(d));
    return d;
  });
  const [left, setLeft] = useState(deadline - Date.now());
  useEffect(() => {
    const id = setInterval(() => setLeft(Math.max(0, deadline - Date.now())), 1000);
    return () => clearInterval(id);
  }, [deadline]);
  const h = Math.floor(left / 3600000);
  const m = Math.floor((left % 3600000) / 60000);
  const s = Math.floor((left % 60000) / 1000);
  return { h, m, s };
}

function Countdown() {
  const { t } = useLang();
  const { h, m, s } = useCountdown();
  const units = [
    { v: h, l: t('masterclass.timerUnits.h') },
    { v: m, l: t('masterclass.timerUnits.m') },
    { v: s, l: t('masterclass.timerUnits.s') },
  ];
  return (
    <div data-testid="countdown-timer" className="inline-block">
      <p className="text-xs font-semibold tracking-widest text-rose-400 mb-4">{t('masterclass.timerTitle')}</p>
      <div className="flex justify-center gap-3">
        {units.map((u, i) => (
          <div key={i} className="rounded-xl border border-rose-500/40 bg-rose-500/10 px-5 py-4 text-center min-w-[84px]">
            <p className="font-display text-4xl sm:text-5xl text-white tabular-nums">{String(u.v).padStart(2, '0')}</p>
            <p className="text-[10px] font-semibold tracking-widest text-slate-400 mt-1">{u.l}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Masterclass() {
  const { t, lang } = useLang();
  const [loading, setLoading] = useState(false);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 100]);

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);

  const buy = async () => {
    setLoading(true);
    try {
      const { data } = await createCheckout({
        lookup_key: 'masterclass_3day',
        origin_url: window.location.origin,
        language: lang,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(t('form.error'));
      setLoading(false);
    }
  };

  const forCards = t('masterclass.forCards');
  const getItems = t('masterclass.getItems');
  const days = t('masterclass.days');
  const achieves = t('masterclass.achieves');
  const us = t('masterclass.us');
  const them = t('masterclass.them');
  const faqs = t('masterclass.faqs');

  const buyBtn = (testid, label, big = false) => (
    <button
      data-testid={testid}
      onClick={buy}
      disabled={loading}
      className={`inline-flex items-center justify-center gap-2 rounded-lg bg-green-500 font-semibold text-white transition-all duration-300 hover:bg-green-400 hover:shadow-[0_0_40px_rgba(34,197,94,0.5)] hover:-translate-y-0.5 active:scale-[0.98] disabled:opacity-60 ${big ? 'px-10 py-5 text-lg' : 'px-8 py-4 text-base'}`}
    >
      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowRight className="w-5 h-5" />}
      {label}
    </button>
  );

  return (
    <main data-testid="masterclass-page" className="pb-20">
      <section ref={heroRef} className="relative min-h-[95vh] flex items-center pt-28 pb-20 overflow-hidden">
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          <img src={IMG_HERO} alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0F172A]/75 via-[#0F172A]/90 to-[#0F172A]" />
        </motion.div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 w-full text-center">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex flex-wrap items-center justify-center gap-3 mb-8"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-500/10 px-4 py-2 text-xs font-semibold text-blue-300" data-testid="mc-badge">
              {t('masterclass.badge')}
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-rose-500/40 bg-rose-500/10 px-4 py-2 text-xs font-semibold text-rose-400" data-testid="mc-urgency">
              <Flame className="w-3.5 h-3.5" />
              {t('masterclass.urgency')}
            </span>
          </motion.div>
          <MaskedLines
            lines={t('masterclass.heroLines')}
            colors={['text-white', 'text-green-400', 'text-blue-400']}
            className="font-display text-5xl sm:text-7xl lg:text-8xl leading-[1.02]"
          />
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="mt-7 max-w-xl mx-auto text-base sm:text-lg text-slate-400 leading-relaxed"
            data-testid="mc-hero-sub"
          >
            {t('masterclass.heroSub')}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-9 flex flex-wrap items-center justify-center gap-4"
          >
            <div className="flex items-end gap-3" data-testid="mc-price">
              <span className="font-display text-6xl sm:text-7xl text-green-400 text-glow-green">{t('masterclass.priceNow')}</span>
              <span className="font-display text-3xl text-slate-500 line-through mb-2">{t('masterclass.priceOld')}</span>
              <span className="mb-2.5 rounded-md bg-rose-500/15 border border-rose-500/40 px-2.5 py-1 text-[11px] font-bold tracking-widest text-rose-400">
                {t('masterclass.discount')}
              </span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-9"
          >
            {buyBtn('mc-hero-buy-btn', t('masterclass.ctaBtn'), true)}
            <div className="mt-5">
              <PayPalButton lookupKey="masterclass_3day" />
            </div>
            <div className="mt-6 inline-flex items-start gap-2.5 rounded-xl border border-amber-400/30 bg-amber-400/5 px-5 py-4 max-w-xl text-left" data-testid="mc-bonus-banner">
              <Gift className="w-5 h-5 mt-0.5 shrink-0 text-amber-400" />
              <p className="text-sm text-amber-200/90">{t('masterclass.bonus')}</p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-24 sm:py-32 bg-[#111D36] border-y border-slate-800" data-testid="mc-about">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-4">
            <div className="relative">
              <div className="absolute -inset-3 rounded-xl border border-green-500/20 pointer-events-none" />
              <img src={IMG_INSTRUCTOR} alt={t('masterclass.aboutOverline')} className="w-full h-[400px] object-cover rounded-xl border border-slate-700/70" loading="lazy" />
            </div>
          </Reveal>
          <div className="lg:col-span-8">
            <SectionHead overline={t('masterclass.aboutOverline')} title={t('masterclass.aboutTitle')} />
            <Reveal delay={0.12}>
              <p className="mt-6 text-slate-300 leading-relaxed max-w-2xl">{t('masterclass.aboutText1')}</p>
              <p className="mt-4 text-slate-400 leading-relaxed max-w-2xl">{t('masterclass.aboutText2')}</p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-32" data-testid="mc-for">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead overline={t('masterclass.forOverline')} title={t('masterclass.forTitle')} align="center" />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {forCards.map((c, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div data-testid={`mc-for-card-${i}`} className="group h-full rounded-xl border border-slate-700/70 bg-[#1E293B] overflow-hidden transition-all duration-300 hover:border-blue-500/60 hover:-translate-y-1.5">
                  <div className="h-44 overflow-hidden">
                    <img src={FOR_IMGS[i]} alt={c.t} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                  </div>
                  <div className="p-7 text-center">
                    <h3 className="font-display text-xl text-white">{c.t}</h3>
                    <p className="mt-3 text-sm text-slate-400 leading-relaxed">{c.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-32 bg-[#111D36] border-y border-slate-800" data-testid="mc-get">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead overline={t('masterclass.getOverline')} title={t('masterclass.getTitle')} align="center" />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {getItems.map((g, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div data-testid={`mc-get-${i}`} className="h-full rounded-xl border border-slate-700/70 bg-[#1E293B] p-7 text-center transition-all duration-300 hover:border-green-500/50 hover:-translate-y-1">
                  <span className="font-display text-4xl text-green-400/40">0{i + 1}</span>
                  <h3 className="mt-3 font-display text-lg text-white">{g.t}</h3>
                  <p className="mt-2.5 text-sm text-slate-400 leading-relaxed">{g.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-32" data-testid="mc-program">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead overline={t('masterclass.progOverline')} title={t('masterclass.progTitle')} align="center" />
          <div className="mt-14 space-y-5">
            {days.map((d, i) => (
              <Reveal key={d.num} delay={i * 0.1}>
                <div data-testid={`mc-day-${i}`} className="group flex gap-6 sm:gap-8 rounded-xl border border-slate-700/70 bg-[#1E293B] p-7 sm:p-9 transition-all duration-300 hover:border-blue-500/60">
                  <span className="font-display text-6xl sm:text-7xl text-blue-500/30 group-hover:text-blue-400/60 transition-colors shrink-0">{d.num}</span>
                  <div>
                    <p className="text-xs font-semibold tracking-widest text-green-400">{d.label}</p>
                    <h3 className="mt-2 font-display text-2xl text-white">{d.t}</h3>
                    <p className="mt-3 text-sm text-slate-400 leading-relaxed">{d.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#111D36] border-y border-amber-400/15" data-testid="mc-special-bonus">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <Reveal>
            <p className="text-xs font-semibold tracking-widest text-amber-400">{t('masterclass.bonusOverline')}</p>
            <p className="mt-2 text-xs text-slate-500">{t('masterclass.bonusNote')}</p>
            <h3 className="mt-5 font-display text-3xl sm:text-5xl text-white">{t('masterclass.bonusTitle')}</h3>
            <p className="mt-5 text-slate-400 max-w-2xl mx-auto leading-relaxed">{t('masterclass.bonusText')}</p>
          </Reveal>
        </div>
      </section>

      <section className="py-24 sm:py-32" data-testid="mc-achieve">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead overline={t('masterclass.achOverline')} title={t('masterclass.achTitle')} align="center" />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {achieves.map((a, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div data-testid={`mc-achieve-${i}`} className="h-full rounded-xl border border-slate-700/70 bg-[#1E293B] p-7 text-center transition-all duration-300 hover:border-blue-500/60 hover:-translate-y-1">
                  <h3 className="font-display text-xl text-blue-300">{a.t}</h3>
                  <p className="mt-3 text-sm text-slate-400">{a.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-32 bg-[#111D36] border-y border-slate-800" data-testid="mc-results">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <SectionHead overline={t('masterclass.resOverline')} title={t('masterclass.resTitle')} align="center" />
          <Reveal delay={0.15}>
            <p className="mt-4 text-center text-slate-400">{t('masterclass.resText')}</p>
            <div className="mt-10 rounded-xl overflow-hidden border border-slate-700/70 shadow-2xl max-w-2xl mx-auto">
              <img src={IMG_CASE} alt={t('masterclass.resTitle')} className="w-full object-cover" loading="lazy" data-testid="mc-case-image" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-24 sm:py-32" data-testid="mc-compare">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead overline={t('masterclass.cmpOverline')} title={t('masterclass.cmpTitle')} align="center" />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            <Reveal>
              <div className="h-full rounded-xl border border-green-500/40 bg-green-500/5 p-8" data-testid="mc-compare-us">
                <h3 className="font-display text-2xl text-green-300">{t('masterclass.usTitle')}</h3>
                <ul className="mt-6 space-y-3.5">
                  {us.map((u, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-slate-200">
                      <Check className="w-4 h-4 mt-0.5 shrink-0 text-green-400" />
                      {u}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="h-full rounded-xl border border-slate-700/70 bg-[#1E293B] p-8 opacity-80" data-testid="mc-compare-them">
                <h3 className="font-display text-2xl text-slate-400">{t('masterclass.themTitle')}</h3>
                <ul className="mt-6 space-y-3.5">
                  {them.map((u, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-slate-500">
                      <X className="w-4 h-4 mt-0.5 shrink-0 text-rose-500/70" />
                      {u}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-32 bg-[#111D36] border-y border-slate-800" data-testid="mc-faq">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <SectionHead overline={t('masterclass.faqOverline')} title={t('masterclass.faqTitle')} align="center" />
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="mt-12 space-y-3" data-testid="mc-faq-accordion">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="rounded-xl border border-slate-700/70 bg-[#1E293B] px-6 data-[state=open]:border-blue-500/50">
                  <AccordionTrigger data-testid={`mc-faq-trigger-${i}`} className="text-left font-display text-base sm:text-lg text-slate-100 hover:text-blue-300 hover:no-underline py-5">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-slate-400 leading-relaxed pb-5">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <section className="py-24 sm:py-28 bg-gradient-to-r from-green-700 via-green-600 to-emerald-700 relative overflow-hidden" data-testid="mc-final-cta">
        <div className="relative max-w-3xl mx-auto px-4 text-center">
          <Reveal>
            <h2 className="font-display text-4xl sm:text-5xl text-white leading-[1.05]">{t('masterclass.finalTitle')}</h2>
            <p className="mt-4 text-xs font-semibold tracking-widest text-green-200">{t('masterclass.badge')}</p>
            <div className="mt-10 flex justify-center">
              <Countdown />
            </div>
            <div className="mt-10 flex items-center justify-center gap-4">
              <span className="font-display text-6xl text-white">{t('masterclass.priceNow')}</span>
              <span className="font-display text-2xl text-green-200/70 line-through">{t('masterclass.priceOld')}</span>
              <span className="rounded-md bg-white/15 border border-white/30 px-2.5 py-1 text-[11px] font-bold tracking-widest text-white">{t('masterclass.discount')}</span>
            </div>
            <div className="mt-9">
              <button
                data-testid="mc-final-buy-btn"
                onClick={buy}
                disabled={loading}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-white px-10 py-5 text-lg font-semibold text-green-700 transition-all duration-300 hover:bg-green-50 hover:shadow-[0_0_45px_rgba(255,255,255,0.35)] hover:-translate-y-0.5 active:scale-[0.98] disabled:opacity-60"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowRight className="w-5 h-5" />}
                {t('masterclass.ctaBtn2')}
              </button>
            </div>
            <p className="mt-6 text-xs text-green-100/80" data-testid="mc-guarantee">{t('masterclass.ps')}</p>
            <div className="mt-6 max-w-sm mx-auto rounded-xl bg-white p-4">
              <PayPalButton lookupKey="masterclass_3day" />
            </div>
          </Reveal>
        </div>
      </section>

      <div className="fixed bottom-0 inset-x-0 z-40 bg-[#0B1220]/90 backdrop-blur-xl border-t border-slate-800 py-3" data-testid="mc-sticky-bar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="font-display text-2xl sm:text-3xl text-green-400">{t('masterclass.priceNow')}</span>
            <span className="font-display text-base text-slate-500 line-through">{t('masterclass.priceOld')}</span>
            <span className="hidden sm:inline rounded-md bg-rose-500/15 border border-rose-500/40 px-2 py-0.5 text-[10px] font-bold tracking-widest text-rose-400">{t('masterclass.discount')}</span>
          </div>
          {buyBtn('mc-sticky-buy-btn', `${t('masterclass.stickyBtn')} ${t('masterclass.priceNow')}`)}
        </div>
      </div>
    </main>
  );
}
